import { useAuthStore } from '@/modules/auth/stores/AuthStore'
import { useCommentStore } from '@/modules/messages/stores/CommentStore'
import { Client, StompSubscription } from '@stomp/stompjs'
import { defineStore } from 'pinia'
import SockJS from 'sockjs-client/dist/sockjs'

const RETRY_WAIT_TIME = 20000
const CONNECTION_TIMEOUT = 10000

let client!: Client
let manualDisconnect = false
// Whether the current client reached CONNECTED since the last drop; failed connect attempts also fire the close handler
let wasConnected = false
let retryTimer: ReturnType<typeof setTimeout> | undefined
// What each topic should be subscribed to; replayed on every (re)connect because STOMP subscriptions die with the socket
const registrations: {
  [topicId: string]: { topic: string; messageCallback: (message: any) => void }
} = {}
const subscriptions: { [topicId: string]: StompSubscription } = {}
// Told when the socket drops without anyone asking for it (never on logout); registered once, outlive any component
const unexpectedCloseListeners = new Set<() => void>()

export const useWebSocketStore = defineStore('websocket', {
  state: (): {} => ({}),
  actions: {
    connectWebSocket() {
      manualDisconnect = false
      // A replaced client must not stay active in the background
      this.clearRetryTimer()
      wasConnected = false
      client?.deactivate()
      client = this.createClient()
      client.activate()
    },

    /** Whether pushed messages can currently arrive (false before the first connect and while reconnecting) */
    isConnected(): boolean {
      return !!client?.connected
    },

    createClient(): Client {
      // deactivate() is async, so a replaced client still fires callbacks after connectWebSocket() installed the next
      // one; every callback must ignore them or it would corrupt the new session's shared state
      const self: Client = new Client({
        webSocketFactory: () => new SockJS(import.meta.env.VITE_BASE_URL + '/socket'),
        reconnectDelay: RETRY_WAIT_TIME,
        connectionTimeout: CONNECTION_TIMEOUT,
        beforeConnect: () => {
          if (self !== client) return
          self.connectHeaders = {
            Authentication: useAuthStore().getToken() || ''
          }
        },
        onConnect: () => {
          if (self !== client) return
          wasConnected = true
          this.initializeSubscriptions()
        },
        onStompError: frame => {
          if (self !== client) return
          console.error(`Broker reported error: ${frame.headers['message']}`)
          console.error(`Additional details: ${frame.body}`)
        },
        onWebSocketClose: () => {
          if (self !== client) return
          // The broker dropped these with the socket; initializeSubscriptions() re-creates them from registrations
          Object.keys(subscriptions).forEach(topicId => delete subscriptions[topicId])
          if (!manualDisconnect) {
            console.warn('WebSocket connection closed unexpectedly. Trying to reconnect...')
            this.retryConnection()
            // Only a connected -> closed transition is news; with the backend down every failed attempt closes again,
            // and listeners that reconnect would turn that into a tight loop that also bypasses the retry delay
            if (wasConnected) {
              wasConnected = false
              this.notifyUnexpectedClose()
            }
          } else {
            console.log('WebSocket disconnected manually. No reconnect attempt.')
          }
        }
      })
      return self
    },

    /** Calls `listener` whenever the socket drops unexpectedly. Returns the unregister function. */
    onUnexpectedClose(listener: () => void): () => void {
      unexpectedCloseListeners.add(listener)
      return () => {
        unexpectedCloseListeners.delete(listener)
      }
    },

    notifyUnexpectedClose() {
      unexpectedCloseListeners.forEach(listener => {
        try {
          listener()
        } catch (error) {
          // One listener's failure must not keep the others (or the reconnect that already ran) from happening
          console.warn('[WebSocketStore] unexpected-close listener failed', error)
        }
      })
    },

    clearRetryTimer() {
      if (retryTimer) clearTimeout(retryTimer)
      retryTimer = undefined
    },

    retryConnection() {
      this.clearRetryTimer()
      retryTimer = setTimeout(() => {
        retryTimer = undefined
        if (!manualDisconnect && !client.connected) {
          console.log('Retrying WebSocket connection...')
          client.activate()
        }
      }, RETRY_WAIT_TIME)
    },

    initializeSubscriptions() {
      // Subscriptions from a previous connection are dead: drop them and subscribe again
      Object.keys(subscriptions).forEach(topicId => delete subscriptions[topicId])
      Object.keys(registrations).forEach(topicId => {
        const { topic, messageCallback } = registrations[topicId]
        subscriptions[topicId] = client.subscribe(topic, messageCallback)
      })
    },

    subscribeToNewAppVersion(callback: Function) {
      const topic = '/topic/new-app-version'

      const messageCallback = (message: any) => {
        const body = this.parseMessageBody(message.body)
        callback(body)
      }

      this.subscribeToTopic(topic, 'new-app-version', messageCallback)
    },

    subscribeToChatTopic(topicId: string) {
      const messageCallback = (message: any) => {
        const body = this.parseMessageBody(message.body)
        this.handleChatMessage(body)
      }

      this.subscribeToTopic(`/topic/chat/space/${topicId}`, topicId, messageCallback)
    },

    handleChatMessage(body: any) {
      const commentStore = useCommentStore()
      switch (body.operation) {
        case 'CREATE':
        case 'UPDATE':
          commentStore.setComment(body.comment)
          break
        case 'DELETE':
          commentStore.updateStateDeleteComment(body.comment)
          break
        default:
          console.warn(`Unhandled operation: ${body.operation}`)
      }
    },

    /**
     * Notifies when a background PDF export finishes (`{ status: 'READY' | 'FAILED', url?, code? }`).
     * Returns a function that cancels the subscription.
     */
    subscribeToPdfExport(jobId: string, callback: (status: any) => void): () => void {
      const topicId = `pdf-export-${jobId}`
      const messageCallback = (message: any) => {
        const body = this.parseMessageBody(message.body)
        if (body) callback(body)
      }

      // Plural, as the backend publishes it (WebSocketService.sendPdfExportEvent)
      this.subscribeToTopic(`/topic/pdf-exports/${jobId}`, topicId, messageCallback)
      return () => this.disconnectWebSocketTopic(topicId)
    },

    subscribeToTopic(topic: string, topicId: string, messageCallback: (message: any) => void) {
      if (registrations[topicId]) return
      registrations[topicId] = { topic, messageCallback }
      // Not connected yet (or ever): initializeSubscriptions() subscribes on connect
      if (client?.connected) {
        subscriptions[topicId] = client.subscribe(topic, messageCallback)
      }
    },

    parseMessageBody(body: string) {
      try {
        return JSON.parse(body)
      } catch (e) {
        console.error('Failed to parse WebSocket message body:', body)
        return null
      }
    },

    disconnectWebSocketTopic(topicId: string) {
      delete registrations[topicId]
      const subscription = subscriptions[topicId]
      delete subscriptions[topicId]
      try {
        subscription?.unsubscribe()
      } catch {
        // After an unexpected close stompjs has no handler left and throws; the broker dropped the subscription with
        // the socket, so there is nothing to cancel and the caller's own cleanup must keep running
      }
    },

    disconnectWebSocket() {
      manualDisconnect = true
      wasConnected = false
      // Nothing may be replayed into the next session's connection
      Object.keys(registrations).forEach(topicId => delete registrations[topicId])
      this.clearRetryTimer()
      Object.keys(subscriptions).forEach(topicId => delete subscriptions[topicId])
      // deactivate() also stops a client that is mid-reconnect (stompjs would keep retrying into the next session)
      // and the broker drops its subscriptions with the socket. Signup/setup may logout before connectWebSocket() ran.
      client?.deactivate()
    }
  }
})

export type WebSocketStore = ReturnType<typeof useWebSocketStore>
